/**
 * LEAVING A RETIREMENT VILLAGE FOR RESIDENTIAL CARE (owner, 7 Oct 2026). What the village pays back
 * when a resident leaves — the exit entitlement — and when, from the terms the profile records:
 *
 *   entitlement = expected resale value (or the entry contribution)
 *                 − departure fee (the deferred management fee: a % a year of the entry or resale
 *                   price, for up to a capped number of years of residency)
 *                 − reinstatement cost
 *                 − the operator's share of any capital gain (the resident keeps their share).
 *
 * The refund arrives on the state's timeline where the unit has not resold sooner: the states set
 * a longstop (retirementVillageStateRules.ts — NSW 6 or 12 months, VIC about 6, QLD and SA 18, WA
 * 12; TAS, ACT and NT set none, so 12 is assumed and the contract decides). Until it arrives the
 * amount owed is an asset of the resident's and the new room's deposit is not yet paid from it.
 *
 * The same arithmetic the village module uses for a move IN (retirementVillageCalculations.ts),
 * here for a move OUT.
 */
import type { FactFind, FactFindRetirementVillage } from '../factFindTypes.ts';
import { computeDmf } from '../retirementVillageCalculations.ts';

export type VillageOnEnteringCare = 'sell' | 'keep_partner_stays' | 'sell_when_partner_leaves';

/**
 * Whether the household still HOLDS a village interest (8 Oct 2026): someone's recorded arrangement
 * is the village, something was paid for it, and the plan has not been paid out (the projection
 * sets `villageInterestSold` on the year's options from the refund). What is held is worth its exit
 * entitlement — in the estate like the family home is, exempt or not for the pension.
 */
/**
 * What a held village interest is worth TODAY — its exit entitlement on today's date, from the
 * profile's terms (blank terms: the full contribution comes back). The screens' snapshot and the
 * server's net worth read the same figure from here, so they cannot disagree.
 */
export function villageInterestToday(factFind: FactFind): number {
    if (!villageInterestHeld(factFind)) return 0;
    const e = villageExitEntitlement(factFind, todayIsoOf(new Date()));
    return e ? Math.max(0, e.entitlement) : 0;
}

/**
 * LEAVING THE VILLAGE FOR ANOTHER VILLAGE OR A GRANNY FLAT (8 Oct 2026). The same rule as leaving
 * for care: the unit is paid out at its exit entitlement on the state's timeline, the money is
 * banked when it arrives (the new entry contribution is paid from savings at the move, and this
 * replenishes them), and the old fees stop. Nothing of it goes to a care deposit, and there is no
 * daily payment to wait on. Null where the household holds no village interest.
 */
export function villageMoveOutPlan(factFind: FactFind, leaveDate: string, destination: 'village' | 'granny_flat'): { person: 'client' | 'partner'; leaveDate: string; refundDate: string; entitlement: number; radAllocation: number; interimDailyPayment: number; destination: 'village' | 'granny_flat'; feesAfterLeaving: { perDay: number; fullDays: number; sharedDays: number; sharePct: number; total: number } } | null {
    if (!((Number(factFind.retirementVillage?.entryContribution) || 0) > 0)) return null;
    const e = villageExitEntitlement(factFind, leaveDate);
    if (!e) return null;
    const feesAfterLeaving = villageFeesAfterLeaving(factFind.retirementVillage, leaveDate, e.refundDate);
    return { person: 'client', leaveDate, refundDate: e.refundDate, entitlement: e.entitlement, radAllocation: 0, interimDailyPayment: 0, destination, feesAfterLeaving: { perDay: feesAfterLeaving.perDay, fullDays: feesAfterLeaving.fullDays, sharedDays: feesAfterLeaving.sharedDays, sharePct: feesAfterLeaving.sharePct, total: feesAfterLeaving.total } };
}

/**
 * THE PAYOUT TAKEN AS PAID AT THE MOVE (owner, 8 Oct 2026). When the client says the sale of the
 * current unit lines up with the new entry — or the new village waits for it — the entitlement is
 * money in the bank on the day of the move, funding the new contribution first. This puts it there:
 * a bank row of its value today. The old record is reset by the caller, so the interest is not held
 * on top of the cash.
 */
export function withVillagePayoutBanked(factFind: FactFind): FactFind {
    const payout = villageInterestToday(factFind);
    if (!(payout > 0)) return factFind;
    const data = structuredClone(factFind) as FactFind;
    if (!data.assets) data.assets = {};
    const rows = Array.isArray(data.assets.bankAccounts) ? data.assets.bankAccounts : [];
    data.assets.bankAccounts = [...rows, { id: 'village-payout', type: 'bankAccounts', description: 'Payout from the current village unit', value: payout, ownership: 'client', income: 0, incomeRate: 0, growthRate: 0 } as never];
    return data;
}

export function villageInterestHeld(factFind: FactFind, strategyOptions: { villageInterestSold?: boolean } = {}): boolean {
    if (strategyOptions.villageInterestSold) return false;
    if (!((Number(factFind.retirementVillage?.entryContribution) || 0) > 0)) return false;
    if (factFind.client?.currentResidency === 'retirement_village') return true;
    return factFind.adviceFor === 'couple' && factFind.partner?.currentResidency === 'retirement_village';
}

/**
 * Whether `person` leaving a retirement village for care means the unit is SOLD back (the exit
 * entitlement paid, the interest gone). A single resident's is; a couple's is where both are
 * entering care, or where the profile says so — the partner staying on keeps it (and keeps them a
 * homeowner by it). Blank for a couple is "the partner stays": the profile asks.
 */
export function villageSellsOnEntry(factFind: FactFind, person: 'client' | 'partner', bothEntering = false): boolean {
    const record = person === 'partner' ? factFind.partner : factFind.client;
    if (record?.currentResidency !== 'retirement_village') return false;
    if (!((Number(factFind.retirementVillage?.entryContribution) || 0) > 0)) return false;
    if (factFind.adviceFor !== 'couple' || bothEntering) return true;
    return factFind.retirementVillage?.onEnteringCare === 'sell';
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole days from one YYYY-MM-DD to another (0 where the second is not after the first). */
export function daysBetween(fromIso: string, toIso: string): number {
    const a = new Date(fromIso + 'T00:00:00').getTime();
    const b = new Date(toIso + 'T00:00:00').getTime();
    if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
    return Math.max(0, Math.round((b - a) / MS_PER_DAY));
}

/**
 * THE INTERIM DAILY PAYMENT. The part of the room the village money will pay is unpaid until the
 * entitlement arrives, and the home charges the daily accommodation payment on it for exactly that
 * long: the room's unpaid balance × the MPIR, a day, for the days between leaving and the refund.
 * A real, one-off cost of leaving a village for care, charged by the projection and stated on every
 * screen that explains the plan.
 */
export function villageInterimDap(radAllocation: number, leaveDate: string, refundDate: string, mpir: number): { days: number; perDay: number; total: number } {
    const days = daysBetween(leaveDate, refundDate);
    const perDay = (Math.max(0, radAllocation) * mpir) / 365;
    return { days, perDay, total: perDay * days };
}

/**
 * Months until the exit entitlement must be paid where the unit has not resold, by state (the
 * longstop). Verified against the primary pages, 8 Oct 2026:
 *   NSW  Retirement Villages Regulation (Sch 5A): 6 months in the listed metropolitan councils, 12
 *        elsewhere (a hard deadline for private villages from July 2026) — the longer is assumed.
 *   VIC  6 months for a non‑owner resident under the current regime; the Retirement Villages
 *        Amendment Act 2025 (in force from 1 May 2026, new contracts only) sets 12 months.
 *   QLD  Retirement Villages Act 1999 s 63: 18 months from leaving, or 14 days after settlement.
 *   SA   Retirement Villages Act 2016, from 2 February 2026: 12 months and 30 business days
 *        (it was 18 months) — taken as 13 months.
 *   WA   12 months (Consumer Protection WA).
 *   ACT  Retirement Villages Act 2012: 18 months.
 *   TAS, NT  no statutory timeframe (Tasmania is drafting reforms): 12 is assumed, the contract decides.
 */
export const VILLAGE_REFUND_MONTHS_BY_STATE: Record<string, number> = {
    NSW: 12,
    VIC: 6,
    QLD: 18,
    SA: 13,
    WA: 12,
    TAS: 12,
    ACT: 18,
    NT: 12,
};
export const VILLAGE_REFUND_MONTHS_DEFAULT = 12;

/**
 * When the entitlement is paid: the contract's months where it names them, else the state's
 * longstop — and sooner where the unit is expected to resell sooner (`expectedResaleMonths`,
 * 8 Oct 2026), since every state pays within days of a resale.
 */
export function villageRefundMonths(village: FactFindRetirementVillage | null | undefined): number {
    const explicit = Number(village?.refundMonths);
    const state = String(village?.state || '').toUpperCase();
    const longstop = Number.isFinite(explicit) && explicit >= 0 ? explicit : (VILLAGE_REFUND_MONTHS_BY_STATE[state] ?? VILLAGE_REFUND_MONTHS_DEFAULT);
    const resale = Number(village?.expectedResaleMonths);
    return Number.isFinite(resale) && resale > 0 ? Math.min(longstop, resale) : longstop;
}

/**
 * THE OLD UNIT'S FEES AFTER LEAVING (8 Oct 2026). Every state lets the village keep charging a
 * departed resident's recurrent charges for a while, until the unit is relicensed; the rule is
 * the state's, verified against the primary pages:
 *   NSW  Retirement Villages Act 1999 s 152 (from 1 Jan 2021): up to 42 days, every resident.
 *   VIC  Retirement Villages Amendment Act 2025: charges cease on vacant possession (new contracts;
 *        assumed for all — the previous regime left it to the contract).
 *   QLD  Retirement Villages Act 1999 s 104: in full for 90 days, then the resident's share (the
 *        proportion they share any resale gain in) until 9 months or the sale.
 *   SA   Retirement Villages Act 2016: recovered from the entitlement for up to 6 months.
 *   WA   Retirement Villages Regulations 1992 reg 9: 3 months (6 for contracts before the
 *        regulation's commencement — the shorter is taken).
 *   ACT  Retirement Villages Act 2012: 6 weeks; an owner then shares the charges in the gain
 *        proportion until the sale.
 *   TAS, NT  no statutory cap: the fees are assumed to run until the payout day (the dearer case).
 * Blank state: the NSW 42 days, as the most common rule, and said to be assumed.
 */
export interface VillageFeesAfterLeavingRule {
    /** Days after leaving charged in full. */
    fullDays: number;
    /** Day after leaving the shared period ends ('untilPaid' = until the payout). 0 = no shared period. */
    sharedUntil: number | 'untilPaid';
    source: string;
}
export const VILLAGE_FEES_AFTER_LEAVING_BY_STATE: Record<string, VillageFeesAfterLeavingRule> = {
    NSW: { fullDays: 42, sharedUntil: 0, source: 'the NSW rule (42 days)' },
    VIC: { fullDays: 0, sharedUntil: 0, source: 'the Victorian rule (none after leaving)' },
    QLD: { fullDays: 90, sharedUntil: 274, source: 'the Queensland rule (90 days, then your share to 9 months)' },
    SA: { fullDays: 183, sharedUntil: 0, source: 'the South Australian rule (up to 6 months)' },
    WA: { fullDays: 91, sharedUntil: 0, source: 'the WA rule (3 months)' },
    ACT: { fullDays: 42, sharedUntil: 'untilPaid', source: 'the ACT rule (6 weeks, then your share until the sale)' },
    TAS: { fullDays: 0, sharedUntil: 'untilPaid', source: 'no Tasmanian limit (assumed until the payout)' },
    NT: { fullDays: 0, sharedUntil: 'untilPaid', source: 'no NT limit (assumed until the payout)' },
};
export const VILLAGE_FEES_AFTER_LEAVING_DEFAULT: VillageFeesAfterLeavingRule = { fullDays: 42, sharedUntil: 0, source: '42 days assumed (no state chosen)' };

export interface VillageFeesAfterLeaving {
    /** The weekly fee as a daily rate. */
    perDay: number;
    fullDays: number;
    sharedDays: number;
    /** The resident's share during the shared period, 0–100. */
    sharePct: number;
    total: number;
    source: string;
}

/** The fees charged between leaving and the payout, on the state's rule, for the days of the wait. */
export function villageFeesAfterLeaving(village: FactFindRetirementVillage | null | undefined, leaveDate: string, refundDate: string): VillageFeesAfterLeaving {
    const perDay = (Math.max(0, Number(village?.ongoingFees) || 0) * 52) / 365;
    const state = String(village?.state || '').toUpperCase();
    const rule = VILLAGE_FEES_AFTER_LEAVING_BY_STATE[state] ?? VILLAGE_FEES_AFTER_LEAVING_DEFAULT;
    const waitDays = daysBetween(leaveDate, refundDate);
    const sharePct = Number.isFinite(Number(village?.capitalGainSharePct)) && village?.capitalGainSharePct != null ? Math.max(0, Math.min(100, Number(village.capitalGainSharePct))) : 50;
    const fullDays = Math.min(waitDays, rule.fullDays);
    const sharedEnd = rule.sharedUntil === 'untilPaid' ? waitDays : Math.min(waitDays, rule.sharedUntil);
    const sharedDays = rule.sharedUntil === 'untilPaid' || rule.sharedUntil > 0 ? Math.max(0, sharedEnd - fullDays) : 0;
    // TAS and NT (no cap) and ACT's owner-share: the "shared" period is in full where no gain share applies to it.
    const effectiveSharePct = rule.sharedUntil === 'untilPaid' && rule.fullDays === 0 ? 100 : sharePct;
    const total = perDay * fullDays + (perDay * sharedDays * effectiveSharePct) / 100;
    return { perDay, fullDays, sharedDays, sharePct: effectiveSharePct, total, source: rule.source };
}

/** What of those fees has accrued `daysSinceLeaving` days after leaving — the projection charges by the year. */
export function villageFeesAfterLeavingAccrued(fees: Pick<VillageFeesAfterLeaving, 'perDay' | 'fullDays' | 'sharedDays' | 'sharePct'>, daysSinceLeaving: number): number {
    const d = Math.max(0, daysSinceLeaving);
    const full = Math.min(d, fees.fullDays);
    const shared = Math.max(0, Math.min(d - fees.fullDays, fees.sharedDays));
    return fees.perDay * full + (fees.perDay * shared * fees.sharePct) / 100;
}

export interface VillageExitEntitlement {
    entryContribution: number;
    resaleValue: number;
    yearsResidency: number;
    departureFee: number;
    reinstatement: number;
    capitalGain: number;
    residentShareOfGain: number;
    operatorShareOfGain: number;
    /** What the resident is paid. */
    entitlement: number;
    refundMonths: number;
    /** YYYY-MM-DD: leaving date plus the refund months. */
    refundDate: string;
    state: string;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** YYYY-MM-DD of a Date, in local time. */
export function todayIsoOf(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function addMonths(iso: string, months: number): string {
    const d = new Date(iso + 'T00:00:00');
    const day = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + Math.round(months));
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, last));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The entitlement on leaving on `leaveDate`, from the profile's village record. Null where there is
 * no village. For a date in the FUTURE (the unit kept while a partner stays on, valued in the
 * estate — 8 Oct 2026), `resaleGrowthPct` grows the expected resale value from `resaleBaseDate`
 * (today) to the leaving date, and the departure fee accrues to that date; where the record gives
 * the years of residency instead of a date, the years to come are added to them.
 */
export function villageExitEntitlement(factFind: FactFind, leaveDate: string, growth: { resaleGrowthPct?: number; resaleBaseDate?: string } = {}): VillageExitEntitlement | null {
    const v = factFind.retirementVillage;
    const entry = Math.max(0, Number(v?.entryContribution) || 0);
    if (!v || entry <= 0 || !ISO.test(leaveDate)) return null;
    const entered = typeof v.dateEntered === 'string' && ISO.test(v.dateEntered) ? new Date(v.dateEntered + 'T00:00:00') : null;
    const leave = new Date(leaveDate + 'T00:00:00');
    const base = growth.resaleBaseDate && ISO.test(growth.resaleBaseDate) ? new Date(growth.resaleBaseDate + 'T00:00:00') : null;
    const yearsAhead = base ? Math.max(0, (leave.getTime() - base.getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 0;
    const yearsResidency = entered
        ? Math.max(0, (leave.getTime() - entered.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
        : Math.max(0, Number(v.yearsResidency) || 0) + yearsAhead;
    const resaleToday = (Number(v.expectedResaleValue) || 0) > 0 ? Number(v.expectedResaleValue) : entry;
    const resaleValue = resaleToday * Math.pow(1 + (Number(growth.resaleGrowthPct) || 0) / 100, yearsAhead);
    const dmfBasis: 'entry' | 'resale' = v.dmfBasis === 'resale' ? 'resale' : 'entry';
    const { dmfAmount } = computeDmf(entry, yearsResidency, Number(v.dmfPctPerYear) || 0, Number(v.dmfCapYears) || 0, dmfBasis, resaleValue);
    const capitalGain = Math.max(0, resaleValue - entry);
    const sharePct = Number.isFinite(Number(v.capitalGainSharePct)) ? Math.max(0, Math.min(100, Number(v.capitalGainSharePct))) : 50;
    const residentShareOfGain = capitalGain * (sharePct / 100);
    const operatorShareOfGain = capitalGain - residentShareOfGain;
    const reinstatement = Math.max(0, Number(v.reinstatementCost) || 0);
    // A capital LOSS is the resident's where the resale is below the entry price (the contract may share it; the simpler, dearer case is assumed).
    const capitalLoss = Math.max(0, entry - resaleValue);
    const entitlement = Math.max(0, entry + residentShareOfGain - capitalLoss - dmfAmount - reinstatement);
    const refundMonths = villageRefundMonths(v);
    return {
        entryContribution: entry,
        resaleValue,
        yearsResidency,
        departureFee: dmfAmount,
        reinstatement,
        capitalGain,
        residentShareOfGain,
        operatorShareOfGain,
        entitlement,
        refundMonths,
        refundDate: addMonths(leaveDate, refundMonths),
        state: String(v.state || ''),
    };
}
